'use client';
import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';

const slideInFromRight = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const slideOutToRight = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

// Exported so BannerSpacer (Header/styles.js) and useNavTopOffset can stay
// in sync with this component's actual rendered height.
export const PROMO_BAR_HEIGHT = 48;

export const StyledRotatingPromoBar = styled.div`
  background-color: ${props => props.theme.colors.get(props.$backgroundColor)};
  color: ${props => props.theme.colors.get(props.$fontColor)};
  font-size: 14px;
  width: 100%;
  display: flex;
  align-items: center;
  height: ${PROMO_BAR_HEIGHT}px;
  overflow: hidden;
  ${props =>
    props.$sticky &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      z-index: 100;
    `}
`;

export const PromoViewport = styled.div`
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

export const MessageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;

  ${props =>
    (props.$entering || props.$exiting) &&
    css`
      position: absolute;
      inset: 0;
      animation: ${props.$entering
          ? props.$direction === 'next'
            ? slideInFromRight
            : slideInFromLeft
          : props.$direction === 'next'
          ? slideOutToLeft
          : slideOutToRight}
        ${props.$animation?.durationMs ?? 500}ms
        ${props.$animation?.easing ?? 'cubic-bezier(0.4, 0, 0.2, 1)'} both;
    `}
`;

export const PromoIcon = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background-color: currentColor;
  mask-image: url(${props => props.$src});
  mask-size: 24px 24px;
  mask-repeat: no-repeat;
  mask-position: center;
`;

export const PromoMessageText = styled.p`
  margin: 0;
  font-weight: inherit;
  display: inline;
`;

export const PromoLinkText = styled.span`
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -1px;
    height: 1.5px;
    width: 100%;
    background-color: currentColor;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 260ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
`;

export const PromoLinkChevron = styled.span`
  display: inline-block;
  margin-left: 4px;
  font-weight: bold;
  transition: transform 260ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const promoLinkStyles = css`
  color: inherit;
  display: inline-flex;
  align-items: center;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  outline: none;

  ${props =>
    props.$isButton
      ? css`
          border: 1px solid currentColor;
          border-radius: 20px;
          padding: 2px 12px;
          margin-left: 8px;
        `
      : css`
          margin-left: 8px;

          &:hover,
          &:focus-visible {
            ${PromoLinkText}::after {
              transform: scaleX(1);
            }

            ${PromoLinkChevron} {
              transform: translateX(3px);
            }
          }
        `}
`;

export const ArrowButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  line-height: 0;
  padding: 0 12px;
  height: 100%;
  display: none;
  align-items: center;
  flex-shrink: 0;

  @media ${props => props.theme.tablet} {
    display: flex;
  }
`;

export const PromoLink = styled(Link)`
  ${promoLinkStyles}
`;

export const PromoLinkButton = styled.button`
  appearance: none;
  background: none;
  font: inherit;
  ${promoLinkStyles}
`;
